import {
  AgentConfigGlobalContexts,
  AgentConfigStep,
  AgentConfigStepAgent,
  AgentConfigStepApiAction,
  AgentConfigStepInputs,
  AgentConfigStepLLMAction,
  AgentConfigStepTypeEnum
} from '@/types/agent';


const RESERVED_KEYWORDS = [
  'global',
  'context',
  'system',
  'config'
];

export type ValidationError = {
  field: string;
  message: string;
};

export type FieldValidationError = {
  stepId: string;
  fieldType: 'input' | 'output' | 'action';
  fieldKey: string;
  routeKey?: string; // for outputs
  actionField?: string; // for action fields like 'model'
  message: string;
};

export type ValidationErrorMap = {
  [stepId: string]: {
    inputs?: { [key: string]: string }; // key -> error message
    outputs?: { [routeKey: string]: { [key: string]: string } }; // route -> output key -> error message
    action?: { [field: string]: string }; // action field -> error message
  };
};

export function validateVariableName( name: string ): ValidationError | null {
  // Check empty
  if ( !name || name.trim() === '' ) {
    return { field: name, message: 'Variable name cannot be empty' };
  }

  // Check reserved keywords
  if ( RESERVED_KEYWORDS.includes( name.toLowerCase() ) ) {
    return { field: name, message: `"${ name }" is a reserved keyword` };
  }

  // Check valid identifier (letters, numbers, underscores, must start with letter or underscore)
  if ( !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test( name ) ) {
    return { field: name, message: 'Must be a valid identifier (letters, numbers, underscores only)' };
  }

  return null;
}

export function validateInputs( inputs: AgentConfigStepInputs, stepId: string ): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  Object.keys( inputs ).forEach( ( key ) => {
    const error = validateVariableName( key );
    if ( error ) {
      errors.push( {
        stepId,
        fieldType: 'input',
        fieldKey: key,
        message: error.message
      } );
    }
  } );

  return errors;
}

export function validateOutputs(
  outputs: Record<string, any>,
  stepId: string,
  routeKey: string
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  Object.keys( outputs ).forEach( ( key ) => {
    const error = validateVariableName( key );
    if ( error ) {
      errors.push( {
        stepId,
        fieldType: 'output',
        fieldKey: key,
        routeKey,
        message: error.message
      } );
    }
  } );

  return errors;
}

export function validateModelAvailability(
  model: string,
  availableModels: string[],
  stepId: string
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  // Check if model exists in available models
  if ( !availableModels.includes( model ) ) {
    errors.push( {
      stepId,
      fieldType: 'action',
      fieldKey: 'model',
      actionField: 'model',
      message: `Model "${ model }" is not available`
    } );
  }

  return errors;
}

export function validateStep(
  step: AgentConfigStep,
  availableModels?: string[],
  globalContext?: AgentConfigGlobalContexts
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  // Validate inputs
  errors.push( ...validateInputs( step.inputs, step.id ) );

  // Validate outputs from all routes
  Object.entries( step.routes ).forEach( ( [ routeKey, route ] ) => {
    errors.push( ...validateOutputs( route.outputs || {}, step.id, routeKey ) );
  } );

  // Build list of available variables (inputs + global context with global. prefix)
  const availableVariables = [
    ...Object.keys( step.inputs ),
    ...Object.keys( globalContext || {} ).map( key => `global.${ key }` )
  ];

  // Validate model availability for LLM steps
  if ( step.type === AgentConfigStepTypeEnum.LLM && availableModels ) {
    const action = step.action as AgentConfigStepLLMAction;
    if ( action && action.model ) {
      errors.push( ...validateModelAvailability( action.model, availableModels, step.id ) );
    }

    // Validate template variables in prompt
    if ( action && action.prompt ) {
      const promptError = validateTemplateVariables( action.prompt, availableVariables );
      if ( promptError ) {
        errors.push( {
          stepId: step.id,
          fieldType: 'action',
          fieldKey: 'prompt',
          actionField: 'prompt',
          message: promptError
        } );
      }
    }
  }

  // Validate template variables in API actions
  if ( step.type === AgentConfigStepTypeEnum.API ) {
    const action = step.action as AgentConfigStepApiAction;

    // Validate URL
    if ( action && action.url ) {
      const urlError = validateTemplateVariables( action.url, availableVariables );
      if ( urlError ) {
        errors.push( {
          stepId: step.id,
          fieldType: 'action',
          fieldKey: 'url',
          actionField: 'url',
          message: urlError
        } );
      }
    }

    // Validate body
    if ( action && action.body ) {
      const bodyError = validateTemplateVariables( action.body, availableVariables );
      if ( bodyError ) {
        errors.push( {
          stepId: step.id,
          fieldType: 'action',
          fieldKey: 'body',
          actionField: 'body',
          message: bodyError
        } );
      }
    }
  }

  // Validate template variables in Agent actions
  if ( step.type === AgentConfigStepTypeEnum.AGENT ) {
    const action = step.action as AgentConfigStepAgent;

    // Validate context_values
    if ( action && action.context_values ) {
      const contextError = validateTemplateVariables( action.context_values, availableVariables );
      if ( contextError ) {
        errors.push( {
          stepId: step.id,
          fieldType: 'action',
          fieldKey: 'context_values',
          actionField: 'context_values',
          message: contextError
        } );
      }
    }
  }

  return errors;
}

export function validateAllSteps(
  steps: AgentConfigStep[],
  availableModels?: string[],
  globalContext?: AgentConfigGlobalContexts
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  steps.forEach( ( step ) => {
    const stepErrors = validateStep( step, availableModels, globalContext );
    errors.push( ...stepErrors );
  } );

  return errors;
}

export function buildValidationErrorMap(
  errors: FieldValidationError[]
): ValidationErrorMap {
  const errorMap: ValidationErrorMap = {};

  errors.forEach( ( error ) => {
    if ( !errorMap[error.stepId] ) {
      errorMap[error.stepId] = {};
    }

    if ( error.fieldType === 'input' ) {
      if ( !errorMap[error.stepId].inputs ) {
        errorMap[error.stepId].inputs = {};
      }
      errorMap[error.stepId].inputs![error.fieldKey] = error.message;
    } else if ( error.fieldType === 'output' ) {
      if ( !errorMap[error.stepId].outputs ) {
        errorMap[error.stepId].outputs = {};
      }
      if ( !errorMap[error.stepId].outputs![error.routeKey!] ) {
        errorMap[error.stepId].outputs![error.routeKey!] = {};
      }
      errorMap[error.stepId].outputs![error.routeKey!][error.fieldKey] = error.message;
    } else if ( error.fieldType === 'action' ) {
      if ( !errorMap[error.stepId].action ) {
        errorMap[error.stepId].action = {};
      }
      errorMap[error.stepId].action![error.fieldKey] = error.message;
    }
  } );

  return errorMap;
}

export function hasValidationErrors( validationErrors: ValidationErrorMap ): boolean {
  return Object.keys( validationErrors ).length > 0;
}

/**
 * Extract template variables from a string (e.g., "Hello {{name}} {{age}}" -> ["name", "age"])
 */
export function extractTemplateVariables( text: string ): string[] {
  const pattern = /\{\{([^{}]+)\}\}/g;
  const matches = text.matchAll( pattern );
  const variables: string[] = [];

  for ( const match of matches ) {
    variables.push( match[1].trim() );
  }

  return variables;
}

/**
 * Validate that template variables in text exist in the available variables list
 */
export function validateTemplateVariables(
  text: string,
  availableVariables: string[]
): string | null {
  const usedVariables = extractTemplateVariables( text );
  const invalidVariables = usedVariables.filter( v => !availableVariables.includes( v ) );

  if ( invalidVariables.length > 0 ) {
    return `Unknown variable(s): ${ invalidVariables.join( ', ' ) }`;
  }

  return null;
}
